import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  Paper,
  TextField,
} from '@mui/material'
import { type InputHTMLAttributes } from 'react'
import { COLORS } from '~theme/colors'
import { CustomPopper } from './multi-item-autocomplete'

export type AutocompleteOnChangeHandlerParams<T> = [
  e: React.SyntheticEvent,
  newValue: string | T | null,
  reason: AutocompleteChangeReason,
  details?: AutocompleteChangeDetails<T>,
]
interface CustomAutocompleteProps<T> extends Partial<
  AutocompleteProps<T, false, false, false>
> {
  options: AutocompleteProps<T, false, false, false>['options']
}

export interface SingleItemAutocomplete<T> {
  input?: string | null
  setInputValue?: (value: string) => void
  label: string
  onChangeHandler: (...args: AutocompleteOnChangeHandlerParams<T>) => void
  options: CustomAutocompleteProps<T>['options']
  hasError?: boolean
  endAdornment?: React.ReactNode
  inputProps?: InputHTMLAttributes<HTMLInputElement>
  helperInput?: string
  helperText?: string
  placeholder?: string
  [key: string]: any
}

export function SingleItemAutocomplete<T>({
  input = '',
  setInputValue,
  label,
  onChangeHandler,
  options,
  hasError,
  endAdornment,
  inputProps,
  disabled,
  helperInput,
  helperText,
  placeholder = 'Search...',
  ...rest
}: SingleItemAutocomplete<T>) {
  return (
    <Autocomplete
      key={`${label}-${rest?.open}`} // Setting the key to force remount when open state changes to reset internal state of Autocomplete
      value={input as T}
      size="small"
      autoSelect
      forcePopupIcon={false}
      options={options}
      onChange={onChangeHandler}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={hasError}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            ...inputProps,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {endAdornment}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      onInputChange={(event, newInputValue) => {
        if (!event && !newInputValue) return
        if (newInputValue !== input) {
          setInputValue?.(newInputValue)
        }
      }}
      disabled={disabled}
      slots={{
        popper: CustomPopper,
        paper: (props) => (
          <Paper
            {...props}
            sx={
              rest?.open && Boolean(helperInput)
                ? {
                    border: `2px solid ${COLORS.secondary.actionBlue}`,
                    borderTop: 'none',
                  }
                : undefined
            }
          />
        ),
      }}
      slotProps={{
        popper: {
          sx: { zIndex: 9999 },
        },
      }}
      {...rest}
    />
  )
}
